import { DatePipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { latestContactDate } from '../../../shared/helpers/assignment.func';
import { TranslocoPipe } from '@jsverse/transloco';
import { CUSTOM_DATE_FORMATS } from '../../adapters/custom-date-format';
import { PersonType } from '../../../api/app-types/person-type.app';
import { LvrDocumentItem } from '../document-item/document-item';
import { DOCUMENT_CATEGORY, DocumentEntry, DocumentFile, DocumentNavItem } from '../../../api/app-types/document.app';
import { Router } from '@angular/router';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { CLARIFICATION_TYPE } from '../../../api/app-types/relative.app';
import { TENANT_FEATURES } from '../../services/tenant-feature.service';

@Component({
	selector: 'lvr-assignment-details',
	templateUrl: './assignment-details.html',
	styleUrl: './assignment-details.scss',
	imports: [DatePipe, TranslocoPipe, LvrDocumentItem],
})
export class AssignmentDetails {
	private readonly router = inject(Router);
	private readonly session = inject(SessionManagerService);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	public readonly legalBaseRequiredInP2Enabled = this.tenantFeatures.legalBaseRequiredInP2Enabled();
	public readonly careRelatedDisabilityTypeRequiredInP2Enabled =
		this.tenantFeatures.careRelatedDisabilityTypeRequiredInP2Enabled();
	public readonly assignment = input.required<AssignmentWithRelations>();
	public readonly clientId = input.required<number>();
	public readonly initiators = input<PersonType[]>([]);
	public readonly latestContact = computed(() => latestContactDate(this.assignment()));
	public readonly hideHeading = input<boolean>(false);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly showP3Fields = computed(
		() =>
			this.assignment().phaseChangeId === DB_PHASES.P3 ||
			this.assignment().phaseId === DB_PHASES.P3 ||
			this.assignment().draftPhaseId === DB_PHASES.P3 ||
			this.assignment().phaseId === DB_PHASES.P4,
	);
	public readonly showP4Fields = computed(() => this.assignment().phaseId === DB_PHASES.P4);
	public readonly showCaseGroup = computed(() => this.tenantFeatures.caseGroupEnabled());
	public readonly isAssignmentInP1 = computed(
		() => this.assignment().phaseId === DB_PHASES.P0 || this.assignment().phaseId === DB_PHASES.P1,
	);
	public readonly hideConsultationDate = computed(() => this.isAssignmentInP1() && this.showP3Fields());
	public readonly showRequiredLegalBaseInP2 = computed(() => {
		const assignment = this.assignment();

		return (
			this.legalBaseRequiredInP2Enabled &&
			(assignment.phaseChangeId === DB_PHASES.P2 ||
				assignment.phaseId === DB_PHASES.P2 ||
				assignment.draftPhaseId === DB_PHASES.P2)
		);
	});
	public readonly showCareRelatedDisabilityTypeInP2 = computed(() => {
		const assignment = this.assignment();
		const targetPhaseId = assignment.phaseChangeId ?? assignment.draftPhaseId ?? assignment.phaseId;

		return this.careRelatedDisabilityTypeRequiredInP2Enabled && targetPhaseId === DB_PHASES.P2;
	});

	public readonly contactPhaseStart = computed(() => {
		const a = this.assignment();
		if (a.contactStart) return a.contactStart;
		if (a.contacts.length > 0) {
			const sortedContacts = [...a.contacts].sort((c1, c2) => c1.contactedAt.getTime() - c2.contactedAt.getTime());
			return sortedContacts[0].contactedAt;
		}
		return undefined;
	});
	public readonly initiator = computed(
		() => this.initiators().find((i) => i.id === this.assignment().initiatorId)?.name,
	);
	public readonly shouldNotBeDeleted = computed(() => this.assignment()?.phaseId === DB_PHASES.P4);
	public readonly canReadAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Read');
	public readonly assignmentPhaseStartDate = computed((): Date | null => {
		const a = this.assignment();
		if (!a) return null;

		const toDate = (value?: Date | string | null): Date | null => {
			if (!value) return null;
			return value instanceof Date ? value : new Date(value);
		};

		switch (a.phaseId) {
			case DB_PHASES.P0:
			case DB_PHASES.P1:
				return toDate(this.contactPhaseStart()) ?? toDate(a.phaseStart);

			case DB_PHASES.P2:
				return toDate(a.consultationDate) ?? toDate(a.phaseStart);

			case DB_PHASES.P3:
				return toDate(a.careStartDate) ?? toDate(a.phaseStart);

			case DB_PHASES.P4:
				return toDate(a.cancellationDate) ?? toDate(a.phaseStart);

			default:
				return toDate(a.phaseStart);
		}
	});

	public readonly clarificationTypes = [
		{ value: CLARIFICATION_TYPE.PHONE, label: 'telefonisch' },
		{ value: CLARIFICATION_TYPE.IN_PERSON, label: 'persönlich' },
		{ value: CLARIFICATION_TYPE.MAIL, label: 'schriftlich / email' },
	];

	public onFileClick(file: DocumentFile) {
		const navItems = this.buildAssignmentNavItems(this.assignment());
		this.router.navigate(['/desk/client', this.clientId(), 'documents', file.id], { state: { navItems } });
	}

	public buildDocumentEntryFromAssignment(doc: DocumentFile): DocumentEntry {
		const assignment = this.assignment() as AssignmentWithRelations;
		const createdAt = this.assignmentPhaseStartDate() ?? new Date();

		return {
			id: assignment.id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt: createdAt,
			externalFileReference: assignment.documents ?? [],
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		};
	}

	private buildAssignmentNavItems(assignmentWithRelations: AssignmentWithRelations | undefined): DocumentNavItem[] {
		const assignment = assignmentWithRelations;
		if (!assignment) return [];

		const files = assignment.documents ?? [];
		const entryName = assignment.phase.remark || '-';
		const createdAt = this.assignmentPhaseStartDate() ?? new Date();

		return files.map((file) => ({
			file,
			entryId: assignment.id,
			entryName,
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt,
			isReport: false,
			isFrozen: this.shouldNotBeDeleted(),
		}));
	}

	public getClarificationLabel(id?: CLARIFICATION_TYPE) {
		const type = this.clarificationTypes.find((t) => t.value === id);
		return type ? type.label : '';
	}
}
